import React from 'react';
import { Citrus, BookOpen, HelpCircle, FlaskConical, AlertTriangle, MessageSquare, Info } from 'lucide-react';
import SaveToIdeasButton from '../components/SaveToIdeasButton';

const VitaminCIVPage = () => {
  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-xl">
              <Citrus className="h-8 w-8 text-orange-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-orange-600 mb-1">Integrative Health</p>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Vitamin C Infusions (Educational)
              </h1>
            </div>
          </div>
          <SaveToIdeasButton
            title="Vitamin C Infusions (IV)"
            category="Vitamins"
            sourcePage="Vitamin C Infusions (Educational)"
            sourceUrl="/vitamin-c-iv"
            variant="compact"
          />
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        
        {/* Section 1: What IV Vitamin C Is */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0 mt-1">
              <BookOpen className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                What is IV Vitamin C?
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Vitamin C (ascorbic acid) is an essential nutrient that plays a role in many bodily functions, including immune support and tissue repair. Most people obtain Vitamin C through diet or oral supplements.
                </p>
                <p>
                  IV (intravenous) Vitamin C is administered directly into the bloodstream, bypassing the digestive system. This allows for much higher blood concentrations than can be achieved through oral intake.
                </p>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-4">
                  <p className="text-slate-700">
                    <span className="font-medium">Oral vs IV:</span> The body limits how much Vitamin C is absorbed through the gut. IV administration can achieve blood levels 50–100 times higher than oral supplementation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Why People with CLL Ask About It */}
        <div className="p-6 md:p-8 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0 mt-1">
              <HelpCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Why people with CLL ask about it
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">IV Vitamin C is sometimes discussed in cancer communities because:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Vitamin C is associated with immune function in general health discussions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Some people explore it as part of broader wellbeing approaches</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>It is offered by some integrative or naturopathic clinics</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Exposure through online communities and patient forums</span>
                  </li>
                </ul>
                <p className="mt-4 text-slate-600 italic">
                  Interest in IV Vitamin C does not mean it is appropriate or beneficial for everyone. Individual circumstances vary.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: What Research Currently Suggests */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0 mt-1">
              <FlaskConical className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                What research currently suggests
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Research into high-dose IV Vitamin C in cancer contexts is ongoing, but current evidence is limited and mixed.
                </p>
                <p className="mb-3">Key points from available research:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>There is no proven benefit for treating CLL or other blood cancers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Some studies explore quality of life effects, but results are inconclusive</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Large-scale clinical trials are lacking</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Research continues in various cancer types, but findings should not be generalised</span>
                  </li>
                </ul>
                <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-4">
                  <p className="font-medium text-slate-800">
                    IV Vitamin C is not a recognised treatment for CLL. Research in this area focuses on exploration, not established benefit.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Important Safety Considerations */}
        <div className="p-6 md:p-8 border-b border-slate-100 bg-red-50/50">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-red-100 rounded-lg flex-shrink-0 mt-1">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Important safety considerations
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  High-dose IV Vitamin C is not appropriate for everyone and carries potential risks that must be carefully considered.
                </p>
                
                <div className="bg-white border border-red-200 rounded-xl p-4 mt-4">
                  <p className="font-medium text-red-800 mb-3">Key safety concerns include:</p>
                  <ul className="space-y-2 text-red-900">
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span><strong>Kidney function:</strong> High doses may stress the kidneys and increase risk of kidney stones</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span><strong>G6PD deficiency:</strong> A genetic condition that can cause severe reactions to high-dose Vitamin C</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span><strong>Medication interactions:</strong> May interfere with certain cancer treatments or medications</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span><strong>Iron overload:</strong> Vitamin C increases iron absorption, which may be problematic for some individuals</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span><strong>Blood test interference:</strong> Can affect accuracy of certain lab results, including glucose monitoring</span>
                    </li>
                  </ul>
                </div>

                <p className="mt-4 font-medium text-slate-800">
                  Screening tests (including G6PD status and kidney function) are typically required before IV Vitamin C is administered.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 5: What to Discuss With Your Healthcare Team */}
        <div className="p-6 md:p-8 bg-green-50/70">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0 mt-1">
              <MessageSquare className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                What to discuss with your healthcare team
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">If you are considering IV Vitamin C, important questions to discuss include:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Is this safe given my current health status and any treatments?</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Have I been tested for G6PD deficiency?</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>How is my kidney function, and will it be monitored?</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Could this interact with any medications I am taking?</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>What is the timing relative to any planned treatments?</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>What realistic expectations should I have?</span>
                  </li>
                </ul>
                <p className="mt-4 font-medium text-slate-800">
                  Any decision about IV Vitamin C should be made in consultation with your haematologist or oncologist, not independently.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Educational Disclaimer Footer */}
      <div className="bg-slate-100 rounded-xl p-5 border border-slate-200">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Educational disclaimer</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              This page provides general educational information only. It does not constitute medical advice, and it does not recommend or promote IV Vitamin C as a treatment. Decisions about any complementary or integrative approach should always involve a qualified healthcare professional familiar with your individual circumstances.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Save Button */}
      <div className="flex justify-center pt-4">
        <SaveToIdeasButton
          title="Vitamin C Infusions (IV)"
          category="Vitamins"
          sourcePage="Vitamin C Infusions (Educational)"
          sourceUrl="/vitamin-c-iv"
        />
      </div>
    </div>
  );
};

export default VitaminCIVPage;
